import type { Product, ProductVariant, OccasionPackage, Order } from '@/data/mockData';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://elavarasiapi.orgininfo.com/api';
const SERVER_URL = process.env.NEXT_PUBLIC_SERVER_URL || 'https://elavarasiapi.orgininfo.com';

export function normalizeImageUrl(url?: string): string {
  if (!url) return '/images/pouch_kai_murukku.png';
  if (url.includes('localhost:5000') || url.includes('localhost:5001') || url.includes('127.0.0.1:5001') || url.includes('127.0.0.1:5000')) {
    url = url.replace(/http:\/\/(localhost|127\.0\.0\.1):(5000|5001)/g, SERVER_URL);
  }
  if (url.startsWith('http://elavarasiapi.orgininfo.com')) {
    url = url.replace('http://elavarasiapi.orgininfo.com', 'https://elavarasiapi.orgininfo.com');
  }
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${SERVER_URL}${url}`;
  if (url.startsWith('/images/')) return url;
  if (url.startsWith('uploads/')) return `${SERVER_URL}/${url}`;
  return url;
}

export function normalizeProduct(raw: any): Product {
  const rawId = raw._id || raw.id || `prod-${Math.random().toString(36).substring(2, 7)}`;
  const rawSlug = raw.slug || rawId;
  const rawCategory = (typeof raw.category === 'object' && raw.category?.slug)
    ? raw.category.slug
    : (typeof raw.mainCategory === 'object' && raw.mainCategory?.slug)
      ? raw.mainCategory.slug
      : (raw.category || 'traditional-sweets');

  let rawVariants: ProductVariant[] = [];
  if (Array.isArray(raw.variants) && raw.variants.length > 0) {
    const seenWeights = new Set<string>();
    raw.variants.forEach((v: any, idx: number) => {
      const weight = (v.weight || v.size || '250g').toString().trim();
      if (!seenWeights.has(weight)) {
        seenWeights.add(weight);
        rawVariants.push({
          weight,
          price: Number(v.price || raw.price || 150),
          stock: Number(v.stock !== undefined ? v.stock : 50),
          sku: v.sku || `${raw.sku || 'EF'}-${weight}-${idx + 1}`,
        });
      }
    });
  } else {
    rawVariants = [
      {
        weight: '250g',
        price: Number(raw.price || 150),
        stock: Number(raw.stock !== undefined ? raw.stock : 50),
        sku: raw.sku || `${rawId}-250g`,
      },
    ];
  }

  let rawIngredients: string[] = [];
  if (Array.isArray(raw.ingredients)) {
    rawIngredients = raw.ingredients;
  } else if (typeof raw.ingredients === 'string' && raw.ingredients.trim()) {
    rawIngredients = raw.ingredients.split(',').map((s: string) => s.trim());
  } else {
    rawIngredients = ['Traditional Fresh Ingredients', 'Cold-Pressed Groundnut Oil', 'Rock Salt'];
  }

  const rawImage = raw.image || raw.images?.[0]?.url || (typeof raw.images?.[0] === 'string' ? raw.images[0] : '/images/pouch_kai_murukku.png');

  return {
    id: rawId,
    name: raw.name || 'Traditional Delicacy',
    tamilName: raw.tamilName || undefined,
    slug: rawSlug,
    category: rawCategory,
    hsnCode: raw.hsnCode || '21069099',
    shortDesc: raw.shortDesc || raw.shortDescription || raw.description?.substring(0, 110) || 'Authentic traditional South Indian recipe.',
    description: raw.description || raw.shortDesc || 'Crafted daily with pure ingredients and heritage recipes.',
    ingredients: rawIngredients,
    shelfLife: raw.shelfLife || '60 Days from packaging',
    storage: raw.storage || 'Store in an airtight container in a cool, dry place away from direct sunlight.',
    isVegetarian: raw.isVegetarian !== false,
    isBestseller: Boolean(raw.isBestseller || raw.featured),
    isFestiveSpecial: Boolean(raw.isFestiveSpecial),
    image: normalizeImageUrl(rawImage),
    rating: Number(raw.rating || raw.averageRating || 4.8),
    reviewsCount: Number(raw.reviewsCount || 120),
    variants: rawVariants,
    bulkTiers: Array.isArray(raw.bulkTiers) ? raw.bulkTiers : [],
    mainCategory: raw.mainCategory,
  } as Product;
}

export function normalizeOrder(raw: any): Order {
  const customerName = raw.customer?.name || raw.customerName || 'Valued Customer';
  const customerEmail = raw.customer?.email || raw.customerEmail || 'orders@elavarasifoods.com';
  const customerPhone = raw.customer?.phone || raw.customerPhone || '+91 98410 00000';
  
  const rawAddress = raw.shippingAddress || {};
  const shippingAddress = {
    address: rawAddress.address || rawAddress.street || 'Central Kitchen',
    city: rawAddress.city || 'Chennai',
    state: rawAddress.state || 'Tamil Nadu',
    pincode: rawAddress.pincode || rawAddress.postalCode || '600001',
  };

  const rawProducts = Array.isArray(raw.products) ? raw.products : (raw.items || []);
  const items = rawProducts.map((p: any) => ({
    productId: p.productId?._id || p.productId || p.id || '',
    name: p.productId?.name || p.name || 'Traditional Delicacy',
    weight: p.size || p.weight || '250g',
    quantity: Number(p.quantity || 1),
    price: Number(p.price || 0),
    hsnCode: p.productId?.hsnCode || '21069099'
  }));

  const totalAmount = Number(raw.pricing?.grandTotal || raw.totalAmount || raw.pricing?.total || 0);
  const subtotal = Number(raw.pricing?.subtotal || raw.subtotal || totalAmount);
  const deliveryFee = Number(raw.pricing?.shippingFee || raw.pricing?.deliveryFee || 0);
  const taxAmount = Number(raw.pricing?.taxTotal || raw.taxAmount || 0);

  const status = String(raw.orderStatus || 'PLACED').toUpperCase();

  const trackingSteps = [
    { title: 'Order Placed & Verified', date: new Date(raw.createdAt || Date.now()).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }), done: true, current: status === 'PLACED' },
    { title: 'Fresh Batch Fried & Moisture Packed', date: 'Same-day Kitchen Dispatch', done: ['CONFIRMED', 'PACKING', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'COMPLETED'].includes(status), current: ['CONFIRMED', 'PACKING', 'PROCESSING'].includes(status) },
    { title: 'Handed over to Express Courier', date: raw.courierPartner || 'Express Air Logistics', done: ['SHIPPED', 'DELIVERED', 'COMPLETED'].includes(status), current: status === 'SHIPPED' },
    { title: 'Out for Doorstep Delivery', date: `${shippingAddress.city}, ${shippingAddress.state}`, done: ['DELIVERED', 'COMPLETED'].includes(status), current: status === 'DELIVERED' }
  ];

  return {
    id: raw._id || raw.id || raw.orderNumber,
    orderNumber: raw.orderNumber || `EF-ORD-${raw._id ? raw._id.substring(0, 6) : '0000'}`,
    customerName,
    customerEmail,
    customerPhone,
    channel: raw.channel || 'B2C_RETAIL',
    items,
    shippingAddress,
    paymentMethod: raw.isCOD ? 'COD' : (raw.paymentMethod || 'ONLINE_UPI'),
    paymentStatus: (raw.paymentStatus === 'COMPLETED' ? 'PAID' : (raw.paymentStatus || 'PAID')),
    orderStatus: status as any,
    subtotal,
    deliveryFee,
    taxAmount,
    totalAmount,
    createdAt: raw.createdAt || new Date().toISOString(),
    trackingSteps
  };
}

export async function fetchLiveProducts(): Promise<Product[]> {
  try {
    const res = await fetch(`${API_BASE}/products?limit=1000`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.products || data.data || []);
    if (Array.isArray(list) && list.length > 0) {
      return list
        .filter((raw: any) => {
          if (raw.productType === 'footwear') return false;
          const nameLower = (raw.name || '').toLowerCase();
          if (nameLower.includes('shoe') || nameLower.includes('loafer') || nameLower.includes('boot') || nameLower.includes('sneaker') || nameLower.includes('harrington')) {
            return false;
          }
          return true;
        })
        .map(normalizeProduct);
    }
  } catch (err: any) {
    if (err?.name !== 'TypeError') {
      console.warn('[Storefront API] Failed to fetch live products:', err);
    }
  }
  return [];
}

export async function fetchLiveCategories(): Promise<Array<{ id: string; name: string; count: number; slug?: string; description?: string; image?: string; isActive?: boolean }>> {
  try {
    const res = await fetch(`${API_BASE}/categories`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.categories || data.data || []);
    if (Array.isArray(list) && list.length > 0) {
      return list.map((c: any) => ({
        id: c.slug || c._id,
        _id: c._id,
        name: c.name,
        slug: c.slug || c._id,
        count: Number(c.productCount || c.count || 6),
        description: c.description || '',
        image: normalizeImageUrl(c.image),
        isActive: c.isActive !== false,
      }));
    }
  } catch (err: any) {
    if (err?.name !== 'TypeError') {
      console.warn('[Storefront API] Failed to fetch live categories:', err);
    }
  }
  return [];
}

export async function fetchProductBySlug(slug: string): Promise<Product | null> {
  try {
    const res = await fetch(`${API_BASE}/products/${slug}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      if (data && (data._id || data.name)) {
        return normalizeProduct(data);
      }
    }
  } catch (err) {
    console.warn(`[Storefront API] Failed to fetch product slug "${slug}":`, err);
  }
  return null;
}

export async function placeStoreOrder(payload: any) {
  const res = await fetch(`${API_BASE}/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Order placement failed: ${res.status} ${errText}`);
  }
  return res.json();
}

export async function fetchLiveHampers(moduleType?: 'occasions' | 'festivals' | 'corporate' | 'all'): Promise<OccasionPackage[]> {
  try {
    const url = moduleType && moduleType !== 'all'
      ? `${API_BASE}/hampers?moduleType=${moduleType}&activeOnly=true`
      : `${API_BASE}/hampers?activeOnly=true`;

    const res = await fetch(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.data || data.hampers || []);
    if (Array.isArray(list) && list.length > 0) {
      return list.map((h: any) => ({
        id: h.slug || h._id,
        _id: h._id,
        title: h.title,
        tamilTitle: h.tamilTitle || '',
        occasionCategory: h.occasionCategory || 'weddings',
        moduleType: h.moduleType || 'occasions',
        occasion: h.occasion || 'Weddings & Engagements',
        subtitle: h.subtitle || '',
        description: h.description || '',
        image: normalizeImageUrl(h.image || h.images?.[0]?.url || (typeof h.images?.[0] === 'string' ? h.images[0] : '') || '/images/royal_gift_box.jpg'),
        badge: h.badge || 'Artisanal Box',
        minOrderQty: Number(h.minOrderQty || 25),
        basePrice: Number(h.basePrice || 350),
        originalPrice: h.basePrice ? Math.round(Number(h.basePrice) * 1.25) : undefined,
        includedSnacks: Array.isArray(h.includedSnacks) ? h.includedSnacks : [],
        packagingType: h.packagingType || 'Royal Keepsake Box',
        idealFor: Array.isArray(h.idealFor) ? h.idealFor : [],
        customizationSupported: Boolean(h.customizationSupported),
        isFestiveSpecial: Boolean(h.isFestiveSpecial),
        isBestseller: Boolean(h.isBestseller),
        slug: h.slug || h.id || h._id,
        includedProducts: Array.isArray(h.includedProducts)
          ? h.includedProducts.map((p: any) => ({
              productId: p.productId?._id || p.productId || undefined,
              name: p.name || p.productId?.name || 'Traditional Delicacy',
              sku: p.sku || p.productId?.sku || '',
              image: normalizeImageUrl(p.image || p.productId?.image),
              price: Number(p.price || p.productId?.price || 0),
              weight: p.weight || 'Standard pack',
              quantity: Number(p.quantity || 1),
            }))
          : [],
        customQuestions: Array.isArray(h.customQuestions)
          ? h.customQuestions.map((q: any, qIdx: number) => ({
              id: q.id || q._id?.toString() || `q_${qIdx}`,
              question: q.question || '',
              type: q.type || 'text',
              options: Array.isArray(q.options)
                ? q.options
                : (typeof q.options === 'string' ? q.options.split(',').map((s: string) => s.trim()).filter(Boolean) : []),
              placeholder: q.placeholder || '',
              required: Boolean(q.required),
            }))
          : [],
      }));
    }
  } catch (err: any) {
    if (err?.name !== 'TypeError') {
      console.warn('[Storefront API] Failed to fetch live hampers:', err);
    }
  }
  return [];
}

export async function fetchHamperBySlug(slugOrId: string): Promise<OccasionPackage | null> {
  try {
    const res = await fetch(`${API_BASE}/hampers/${encodeURIComponent(slugOrId)}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const json = await res.json();
      const h = json.data || json;
      if (h && (h.title || h.name)) {
        return {
          id: h.slug || h._id,
          _id: h._id,
          slug: h.slug || h._id,
          title: h.title || h.name,
          tamilTitle: h.tamilTitle || '',
          occasionCategory: h.occasionCategory || 'weddings',
          moduleType: h.moduleType || 'occasions',
          occasion: h.occasion || 'Weddings & Engagements',
          subtitle: h.subtitle || '',
          description: h.description || '',
          image: normalizeImageUrl(h.image),
          badge: h.badge || 'Artisanal Box',
          minOrderQty: Number(h.minOrderQty || 25),
          basePrice: Number(h.basePrice || 350),
          originalPrice: h.basePrice ? Math.round(Number(h.basePrice) * 1.25) : undefined,
          includedSnacks: Array.isArray(h.includedSnacks) ? h.includedSnacks : [],
          packagingType: h.packagingType || 'Royal Keepsake Box',
          idealFor: Array.isArray(h.idealFor) ? h.idealFor : [],
          customizationSupported: Boolean(h.customizationSupported),
          isFestiveSpecial: Boolean(h.isFestiveSpecial),
          isBestseller: Boolean(h.isBestseller),
          includedProducts: Array.isArray(h.includedProducts)
            ? h.includedProducts.map((p: any) => ({
                productId: p.productId?._id || p.productId || undefined,
                name: p.name || p.productId?.name || 'Traditional Delicacy',
                sku: p.sku || p.productId?.sku || '',
                image: normalizeImageUrl(p.image || p.productId?.image),
                price: Number(p.price || p.productId?.price || 0),
                weight: p.weight || 'Standard pack',
                quantity: Number(p.quantity || 1),
              }))
            : [],
          customQuestions: Array.isArray(h.customQuestions)
            ? h.customQuestions.map((q: any, qIdx: number) => ({
                id: q.id || q._id?.toString() || `q_${qIdx}`,
                question: q.question || '',
                type: q.type || 'text',
                options: Array.isArray(q.options)
                  ? q.options
                  : (typeof q.options === 'string' ? q.options.split(',').map((s: string) => s.trim()).filter(Boolean) : []),
                placeholder: q.placeholder || '',
                required: Boolean(q.required),
              }))
            : [],
        };
      }
    }
  } catch (err) {
    console.warn('[Storefront API] fetchHamperBySlug direct failed, trying fallback:', err);
  }

  // Fallback: search in live hampers list
  const allHampers = await fetchLiveHampers('all');
  const found = allHampers.find((h) => h.slug === slugOrId || h.id === slugOrId || h._id === slugOrId);
  return found || null;
}

export async function submitOccasionQuote(quotePayload: any): Promise<any> {
  const res = await fetch(`${API_BASE}/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(quotePayload),
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.message || `Quotation submission failed: ${res.status}`);
  }

  return res.json();
}

export interface HamperCategoryItem {
  id: string;
  _id?: string;
  label: string;
  tamilLabel?: string;
  value: string;
  icon?: string;
  description?: string;
  image: string;
  moduleType?: string;
  defaultBadge?: string;
  defaultPackaging?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export async function fetchLiveHamperCategories(type?: 'occasions' | 'festivals' | 'corporate' | 'all'): Promise<HamperCategoryItem[]> {
  try {
    const query = type && type !== 'all' ? `?type=${type}` : '';
    const res = await fetch(`${API_BASE}/hampers/categories${query}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const list = Array.isArray(json) ? json : (json.data || []);
    if (Array.isArray(list) && list.length > 0) {
      return list.map((c: any) => ({
        id: c.value || c._id || `hcat-${Math.random().toString(36).substring(2, 7)}`,
        _id: c._id,
        label: c.label || 'Occasion Collection',
        tamilLabel: c.tamilLabel || '',
        value: c.value || c.label?.toLowerCase()?.replace(/\s+/g, '-') || 'general',
        icon: c.icon || '🎁',
        description: c.description || '',
        image: normalizeImageUrl(c.image || '/images/occasion_wedding_toon.jpg'),
        moduleType: c.moduleType || 'occasions',
        defaultBadge: c.defaultBadge || '',
        defaultPackaging: c.defaultPackaging || '',
        sortOrder: c.sortOrder || 0,
        isActive: c.isActive !== false,
      }));
    }
  } catch (err: any) {
    if (err?.name !== 'TypeError') {
      console.warn('[Storefront API] Failed to fetch live hamper categories:', err);
    }
  }
  return [];
}

export async function fetchLiveOrder(query: string): Promise<Order | null> {
  const clean = encodeURIComponent(query.trim());
  try {
    const res = await fetch(`${API_BASE}/orders/public/number/${clean}`, { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      return normalizeOrder(data);
    }
  } catch (e) {}

  try {
    const res = await fetch(`${API_BASE}/orders/track/${clean}`, { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      return normalizeOrder(data);
    }
  } catch (e) {}

  return null;
}

/* ------------------------------------------------------------------ */
/*  Customer Authentication & Profile API                             */
/* ------------------------------------------------------------------ */

export interface CustomerAuthResponse {
  _id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
  profileImage?: string;
  loyaltyPoints?: number;
  totalOrders?: number;
  totalSpent?: number;
  token: string;
  accessToken?: string;
  message?: string;
}

export async function loginWithGoogleOAuth(payload: {
  idToken: string;
  name?: string;
  email?: string;
  picture?: string;
  googleId?: string;
}): Promise<CustomerAuthResponse> {
  const res = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Google authentication failed');
  }
  return data;
}

export async function loginWithCredentials(
  email: string,
  password: string
): Promise<CustomerAuthResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Invalid email or password');
  }
  return data;
}

export async function registerCustomer(payload: {
  name: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
}): Promise<CustomerAuthResponse> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Registration failed');
  }
  return data;
}

export async function fetchCustomerProfile(token: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const res = await fetch(`${API_BASE}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!res.ok) {
      const err: any = new Error(`Failed to fetch customer profile (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  } catch (e) {
    clearTimeout(timeoutId);
    throw e;
  }
}

export async function updateCustomerProfile(
  token: string,
  updates: { name?: string; phone?: string; profileImage?: string }
) {
  const res = await fetch(`${API_BASE}/auth/profile/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update profile');
  return res.json();
}

export async function fetchCustomerOrders(token: string): Promise<Order[]> {
  try {
    const res = await fetch(`${API_BASE}/orders/history`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    const ordersArray = Array.isArray(data) ? data : data.orders || [];
    return ordersArray.map(normalizeOrder);
  } catch {
    return [];
  }
}

export async function fetchCustomerAddresses(token: string) {
  try {
    const res = await fetch(`${API_BASE}/address`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    });
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}

export async function createCustomerAddress(token: string, addressData: any) {
  const res = await fetch(`${API_BASE}/address`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(addressData),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create address');
  }
  return res.json();
}

export async function deleteCustomerAddress(token: string, addressId: string) {
  const res = await fetch(`${API_BASE}/address/${addressId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete address');
  return res.json();
}
